import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { formatBreakEven, formatMoney, formatPercent } from "@shared/roi/format";
import type { ScenarioRow, VisibilityName } from "@shared/schemas/scenario";
import { Badge, type BadgeKind } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { formatRelative } from "@client/lib/datetime";

/**
 * The scenario list for one product: metadata plus the denormalised KPIs the
 * server stores on save, never a recomputation. A list that recalculated twenty
 * projections to draw itself would be a slow list that disagrees with the
 * editor.
 *
 * Sorting is client-side over the page already fetched. That is honest at this
 * size — a product with more scenarios than one page gets a server-side sort
 * when it exists, not a sort that silently covers the first fifty.
 */

const VISIBILITY: Record<VisibilityName, { label: string; kind: BadgeKind; title: string }> = {
  PRIVATE: {
    label: "Private",
    kind: "neutral",
    title: "Only the author, product owners and administrators can read this.",
  },
  SHARED: {
    label: "Shared",
    kind: "info",
    title: "The author, product owners, administrators and everyone it is shared with.",
  },
  PRODUCT: {
    label: "Product-wide",
    kind: "info",
    title: "Anyone who can see this product can read it.",
  },
};

type SortKey = "name" | "author" | "roi" | "breakEven" | "net" | "updated";

interface SortState {
  key: SortKey;
  direction: "asc" | "desc";
}

/** Nulls last in both directions: "not computed yet" is not a small number. */
function compareNullable(a: number | null, b: number | null, direction: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * direction;
}

function sortScenarios(rows: readonly ScenarioRow[], sort: SortState): ScenarioRow[] {
  const direction = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    // The baseline stays on top however the rest is sorted — it is the thing
    // most people came to fork, not just another row.
    if (a.isBaseline !== b.isBaseline) return a.isBaseline ? -1 : 1;

    switch (sort.key) {
      case "name":
        return a.name.localeCompare(b.name) * direction;
      case "author":
        return a.author.fullName.localeCompare(b.author.fullName) * direction;
      case "roi":
        return compareNullable(a.summary.roiPct, b.summary.roiPct, direction);
      case "breakEven":
        return compareNullable(a.summary.breakEvenYears, b.summary.breakEvenYears, direction);
      case "net":
        return compareNullable(a.summary.net, b.summary.net, direction);
      case "updated":
        return (Date.parse(a.updatedAt) - Date.parse(b.updatedAt)) * direction;
    }
  });
}

interface ScenarioTableProps {
  scenarios: readonly ScenarioRow[];
  productSlug: string;
  currencyCode: string;
  onDuplicate: (scenario: ScenarioRow) => void;
  onShare: (scenario: ScenarioRow) => void;
  onArchive: (scenario: ScenarioRow) => void;
  onSetBaseline: (scenario: ScenarioRow) => void;
  /** The id of the row a mutation is in flight for, so only its buttons wait. */
  busyId?: string | null;
  /**
   * Compare selection. Both are given together or neither is: without a handler
   * the column is not rendered at all, because a checkbox that does nothing is
   * worse than no checkbox.
   */
  selectedIds?: readonly string[];
  onToggleSelect?: (scenario: ScenarioRow) => void;
  /** False for an unticked row once the comparison is full — see docs/08. */
  canSelectMore?: boolean;
}

export function ScenarioTable({
  scenarios,
  productSlug,
  currencyCode,
  onDuplicate,
  onShare,
  onArchive,
  onSetBaseline,
  busyId,
  selectedIds,
  onToggleSelect,
  canSelectMore = true,
}: ScenarioTableProps) {
  const selectable = onToggleSelect !== undefined;
  const selected = new Set(selectedIds ?? []);
  const [sort, setSort] = useState<SortState>({ key: "updated", direction: "desc" });
  const rows = useMemo(() => sortScenarios(scenarios, sort), [scenarios, sort]);

  function toggleSort(key: SortKey) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : // Text reads best A–Z; figures and dates best biggest-first.
          { key, direction: key === "name" || key === "author" ? "asc" : "desc" },
    );
  }

  // `aria-sort` belongs on the column header, and only on the one actually
  // sorted — every other column must say "none", not "ascending by default".
  const ariaSort = (key: SortKey): "ascending" | "descending" | "none" =>
    sort.key === key ? (sort.direction === "asc" ? "ascending" : "descending") : "none";

  const header = (key: SortKey, label: string, align: "left" | "right" = "left") => (
    <th
      scope="col"
      aria-sort={ariaSort(key)}
      className={align === "right" ? "px-3 py-2 text-right" : "px-3 py-2 text-left"}
    >
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-ink-soft hover:text-ink"
      >
        {label}
        <span aria-hidden="true" className={sort.key === key ? "" : "opacity-0"}>
          {sort.direction === "asc" ? "▲" : "▼"}
        </span>
      </button>
    </th>
  );

  return (
    // The table scrolls inside its own container, so the page never scrolls
    // sideways on a phone (docs/08-frontend.md).
    <div className="-mx-2 overflow-x-auto px-2">
      <table className="w-full min-w-[56rem] border-collapse text-sm">
        <thead className="border-b border-line">
          <tr>
            {selectable && (
              <th scope="col" className="w-9 px-3 py-2">
                <span className="sr-only">Compare</span>
              </th>
            )}
            {header("name", "Scenario")}
            {header("author", "Author")}
            <th scope="col" className="px-3 py-2 text-left">
              <span className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">
                Visibility
              </span>
            </th>
            {header("roi", "ROI", "right")}
            {header("breakEven", "Break-even", "right")}
            {header("net", "Net", "right")}
            {header("updated", "Updated", "right")}
            <th scope="col" className="px-3 py-2 text-right">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((scenario) => {
            const busy = busyId === scenario.id;
            const visibility = VISIBILITY[scenario.visibility];
            const net = scenario.summary.net;

            const isSelected = selected.has(scenario.id);

            return (
              <tr key={scenario.id} className="border-line/60 border-b last:border-0">
                {selectable && (
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      // A full comparison locks the unticked rows rather than
                      // silently dropping the oldest choice when a fifth is
                      // ticked.
                      disabled={!isSelected && !canSelectMore}
                      onChange={() => onToggleSelect?.(scenario)}
                      aria-label={`Compare ${scenario.name}`}
                      title={
                        !isSelected && !canSelectMore
                          ? "A comparison holds four scenarios. Untick one to swap it."
                          : "Add this scenario to a comparison"
                      }
                      className="h-4 w-4 accent-[var(--azure-deep)]"
                    />
                  </td>
                )}
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/products/$slug/scenarios/$scenarioId"
                      params={{ slug: productSlug, scenarioId: scenario.id }}
                      className="font-semibold text-azure-deep hover:underline"
                    >
                      {scenario.name}
                    </Link>
                    {scenario.isBaseline && <Badge kind="info">Baseline</Badge>}
                    {scenario.archivedAt && <Badge kind="warning">Archived</Badge>}
                  </div>
                  {scenario.description && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-ink-soft">
                      {scenario.description}
                    </p>
                  )}
                </td>

                <td className="px-3 py-2.5 text-ink-soft">{scenario.author.fullName}</td>

                <td className="px-3 py-2.5">
                  <Badge kind={visibility.kind} title={visibility.title}>
                    {visibility.label}
                  </Badge>
                  {scenario.shareCount > 0 && (
                    <span className="tabular ml-1.5 text-[11px] text-ink-soft">
                      +{scenario.shareCount}
                    </span>
                  )}
                </td>

                <td className="tabular px-3 py-2.5 text-right font-semibold text-ink">
                  {scenario.summary.roiPct === null ? (
                    <span className="font-normal text-ink-soft">—</span>
                  ) : (
                    formatPercent(scenario.summary.roiPct)
                  )}
                </td>

                <td className="tabular px-3 py-2.5 text-right text-ink-soft">
                  {scenario.summary.roiPct === null
                    ? "—"
                    : formatBreakEven(scenario.summary.breakEvenYears, scenario.horizonYears)}
                </td>

                <td
                  className="tabular px-3 py-2.5 text-right font-semibold"
                  style={{
                    color:
                      net === null
                        ? undefined
                        : net >= 0
                          ? "var(--roi-revenue)"
                          : "var(--roi-cost)",
                  }}
                >
                  {net === null ? (
                    <span className="font-normal text-ink-soft">—</span>
                  ) : (
                    formatMoney(currencyCode, net, { compact: true, signed: true })
                  )}
                </td>

                <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs text-ink-soft">
                  {formatRelative(scenario.updatedAt)}
                </td>

                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="quiet"
                      size="sm"
                      onClick={() => onDuplicate(scenario)}
                      disabled={busy}
                      title="Take your own editable copy"
                    >
                      Duplicate
                    </Button>
                    {scenario.abilities.canShare && (
                      <Button
                        variant="quiet"
                        size="sm"
                        onClick={() => onShare(scenario)}
                        disabled={busy}
                      >
                        Share
                      </Button>
                    )}
                    {scenario.abilities.canSetBaseline && !scenario.isBaseline && (
                      <Button
                        variant="quiet"
                        size="sm"
                        onClick={() => onSetBaseline(scenario)}
                        disabled={busy}
                        title="Make this the profile new scenarios fork from"
                      >
                        Set baseline
                      </Button>
                    )}
                    {scenario.abilities.canArchive && (
                      <Button
                        variant="quiet"
                        size="sm"
                        onClick={() => onArchive(scenario)}
                        disabled={busy}
                      >
                        {scenario.archivedAt ? "Restore" : "Archive"}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
